import React, { useState } from "react";
import DOMPurify from "dompurify";
import { SYMBOL_CATEGORIES } from "../../constants/symbols";
import {
  mutSetLayerVisible, mutSetLayerLocked, mutSetLayerColor,
  mutAddLayer, mutDeleteLayer, mutRenameLayer,
  mutSaveLayerState, mutRestoreLayerState, mutDeleteLayerState,
} from "../../engine/layerMutations";

const sanitizeSVG = raw =>
  DOMPurify.sanitize(raw || "", { USE_PROFILES: { svg: true, svgFilters: true } });

const LAYER_ICONS = {
  walls: "▬", openings: "🚪", rooms: "⬛", electrical: "⚡",
  plumbing: "💧", furniture: "🪑", structural: "⬜", annotations: "✏",
  dimensions: "↔", site: "▭",
};

const SECTION_TABS = [
  { id: "layers",     label: "Layers",    icon: "◫" },
  { id: "objects",    label: "Objects",   icon: "⊞" },
  { id: "symbols",    label: "Symbols",   icon: "⚡" },
  { id: "states",     label: "States",    icon: "⟳" },
  { id: "properties", label: "Props",     icon: "≡" },
];

function LayerRow({ layer, isActive, onSetActive, onToggleVisible, onToggleLock, onColorChange, onDelete, onRename }) {
  const [hovered, setHovered] = useState(false);
  const [editing, setEditing] = useState(false);
  const [nameVal, setNameVal] = useState(layer.name);

  const commitRename = () => {
    setEditing(false);
    if (nameVal.trim() && nameVal !== layer.name) onRename(layer.id, nameVal.trim());
  };

  return (
    <div
      onMouseOver={() => setHovered(true)}
      onMouseOut={() => setHovered(false)}
      onClick={() => !editing && onSetActive(layer.id)}
      style={{
        display: "flex", alignItems: "center", gap: 5,
        padding: "4px 8px 4px 6px",
        background: isActive ? "#0c1c38" : hovered ? "#08111e" : "transparent",
        borderLeft: `3px solid ${isActive ? layer.color : "transparent"}`,
        cursor: "pointer",
        opacity: layer.visible ? 1 : 0.4,
        transition: "background 0.1s",
      }}>
      {/* Color swatch / picker */}
      <input type="color" value={layer.color}
        onChange={e => { e.stopPropagation(); onColorChange(layer.id, e.target.value); }}
        onClick={e => e.stopPropagation()}
        style={{ width: 14, height: 14, border: "none", background: "none",
          cursor: "pointer", padding: 0, borderRadius: 2, flexShrink: 0 }}
      />

      {/* Icon */}
      <span style={{ fontSize: 10, width: 14, textAlign: "center", flexShrink: 0 }}>
        {LAYER_ICONS[layer.id] || "●"}
      </span>

      {/* Name — dbl-click to rename (user layers only) */}
      {editing ? (
        <input autoFocus value={nameVal}
          onChange={e => setNameVal(e.target.value)}
          onBlur={commitRename}
          onKeyDown={e => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setEditing(false); }}
          onClick={e => e.stopPropagation()}
          style={{ flex:1, fontSize:10, background:"#08111e", border:"1px solid #3b82f640",
            color:"#dde6f0", borderRadius:2, padding:"1px 4px", fontFamily:"inherit" }}
        />
      ) : (
        <span
          onDoubleClick={e => { e.stopPropagation(); if (layer.type !== "system") setEditing(true); }}
          style={{
            flex: 1, fontSize: 11, fontFamily: "'JetBrains Mono',monospace",
            color: isActive ? "#dde6f0" : "#6b7e94",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
          {layer.name}
        </span>
      )}

      {/* Controls — only show on hover or active */}
      {(hovered || isActive) && (
        <div style={{ display: "flex", gap: 2, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          <button title={layer.locked ? "Unlock" : "Lock"}
            onClick={() => onToggleLock(layer.id, !layer.locked)}
            style={{ ...iconBtn, color: layer.locked ? "#f97316" : "#2a3a4a" }}>
            {layer.locked ? "🔒" : "🔓"}
          </button>
          <button title={layer.visible ? "Hide" : "Show"}
            onClick={() => onToggleVisible(layer.id, !layer.visible)}
            style={{ ...iconBtn, color: layer.visible ? "#4ade80" : "#2a3a4a" }}>
            {layer.visible ? "●" : "○"}
          </button>
          {layer.type !== "system" && (
            <button title="Delete layer"
              onClick={() => onDelete(layer.id)}
              style={{ ...iconBtn, color: "#f87171" }}>
              ×
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const iconBtn = {
  background: "transparent", border: "none", cursor: "pointer",
  fontSize: 11, padding: "0 2px", lineHeight: 1,
  fontFamily: "'JetBrains Mono',monospace",
};

// ── Objects panel — lists all objects in the scene ─────────────────────────
function ObjectsPanel({ st, sel, onSelect }) {
  const walls  = Object.values(st.edges || {});
  const syms   = Object.values(st.symbols || {});
  const anns   = Object.values(st.annotations || {});
  const dims   = Object.values(st.manualDims || {});
  const gls    = Object.values(st.gridLines || {});

  const groups = [
    { label: "Walls",       icon:"▬", items: walls.map(e => ({ id:e.id, label:`Wall ${e.materialId||"BRICK"} ${e.thickness}mm`, type:"edge" })) },
    { label: "Rooms",       icon:"⬛", items: Object.keys(st.faces||{}).map(id => { const r=(st.rooms||{})[id]; return { id, label:r?.name||r?.type||"Room", type:"face" }; }) },
    { label: "Symbols",     icon:"⚡", items: syms.map(s => ({ id:s.id, label:s.type+(s.label?` (${s.label})`:""), type:"symbol" })) },
    { label: "Annotations", icon:"✏", items: anns.map(a => ({ id:"ann_"+a.id, label:a.text?.slice(0,20)||"annotation", type:"ann" })) },
    { label: "Dimensions",  icon:"↔", items: dims.map(d => ({ id:"dim_"+d.id, label:"Dim", type:"dim" })) },
    { label: "Grid Lines",  icon:"┼", items: gls.map(g => ({ id:g.id, label:`${g.axis.toUpperCase()} ${g.label}`, type:"gl" })) },
  ].filter(g => g.items.length > 0);

  return (
    <div style={{ overflowY: "auto", flex: 1 }}>
      {groups.map(g => (
        <div key={g.label}>
          <div style={{ fontSize: 9, color: "#2a3a4a", letterSpacing: 1.5, fontWeight: 800,
            padding: "6px 10px 3px", fontFamily: "'JetBrains Mono',monospace",
            display: "flex", alignItems: "center", gap: 5 }}>
            <span>{g.icon}</span>{g.label.toUpperCase()}
            <span style={{ color: "#1e2a36", fontWeight: 400 }}>({g.items.length})</span>
          </div>
          {g.items.map(item => (
            <div key={item.id} onClick={() => onSelect(item)}
              style={{
                padding: "3px 10px 3px 24px", fontSize: 10.5, cursor: "pointer",
                fontFamily: "'JetBrains Mono',monospace",
                color: sel?.id === item.id ? "#60a5fa" : "#4a5e70",
                background: sel?.id === item.id ? "#0c1828" : "transparent",
              }}
              onMouseOver={e => e.currentTarget.style.background = "#08111e"}
              onMouseOut={e => e.currentTarget.style.background = sel?.id === item.id ? "#0c1828" : "transparent"}>
              {item.label}
            </div>
          ))}
        </div>
      ))}
      {groups.length === 0 && (
        <div style={{ padding: 16, fontSize: 10, color: "#1e2a36", textAlign: "center" }}>
          No objects yet
        </div>
      )}
    </div>
  );
}

// ── Symbol picker ─────────────────────────────────────────────────────────────
function SymbolsPanel({ activeSymbol, onPickSymbol }) {
  const [cat, setCat] = useState("Electrical");
  const activeCat = SYMBOL_CATEGORIES.find(c => c.id === cat);
  return (
    <div style={{ display:"flex", flexDirection:"column", flex:1, overflow:"hidden" }}>
      {/* Category row */}
      <div style={{ display:"flex", borderBottom:"1px solid #0d1520", flexShrink:0 }}>
        {SYMBOL_CATEGORIES.map(c => (
          <button key={c.id} onClick={() => setCat(c.id)}
            style={{ flex:1, padding:"5px 2px", fontSize:8, border:"none",
              background: cat===c.id ? "#08111e" : "transparent",
              borderBottom: `2px solid ${cat===c.id ? c.color : "transparent"}`,
              color: cat===c.id ? c.color : "#2a3a4a",
              cursor:"pointer", fontFamily:"inherit", fontWeight:700 }}>
            {c.id.slice(0,4).toUpperCase()}
          </button>
        ))}
      </div>
      {/* Symbol grid */}
      <div style={{ overflowY:"auto", flex:1, padding:"8px 6px",
        display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:5 }}>
        {(activeCat?.symbols||[]).map(sym => {
          const isAct = activeSymbol === sym.id;
          return (
            <button key={sym.id} onClick={() => onPickSymbol(isAct ? null : sym.id)}
              title={sym.label}
              style={{ padding:"6px 2px 4px", background: isAct ? "#0c1828" : "#06111e",
                border:`1px solid ${isAct ? (activeCat?.color||"#3b82f6")+"80" : "#0d1520"}`,
                borderRadius:4, cursor:"pointer",
                display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
              <svg width="28" height="28" viewBox="0 0 100 100"
                style={{ color: activeCat?.color || "#facc15" }}>
                <g fill="none" stroke="currentColor" strokeWidth="6"
                  dangerouslySetInnerHTML={{ __html: sanitizeSVG(sym.svgPath) }}/>
              </svg>
              <span style={{ fontSize:7, color:isAct?(activeCat?.color||"#60a5fa"):"#2a3a4a",
                textAlign:"center", lineHeight:1.2, fontFamily:"inherit",
                overflow:"hidden", textOverflow:"ellipsis", width:"100%",
                display:"block" }}>
                {sym.label.split(' ').slice(0,2).join(' ')}
              </span>
            </button>
          );
        })}
      </div>
      {activeSymbol && (
        <div style={{ padding:"5px 8px", background:"#0c1828", borderTop:"1px solid #0d1520",
          fontSize:9, color:"#60a5fa", textAlign:"center", flexShrink:0 }}>
          Click canvas to place · Esc cancel
        </div>
      )}
    </div>
  );
}

// ── Main CAD panel ─────────────────────────────────────────────────────────────
export default function LayerPanelCAD({
  st, commit, sel, selAnnotation,
  activeSymbol, onPickSymbol, onSelectObject,
  activeLayer, onSetActiveLayer,
}) {
  const [tab, setTab] = useState("layers");
  const [newName, setNewName] = useState("");

  const layers = st.layers || {};
  const system = Object.values(layers).filter(l => l.type === "system");
  const user   = Object.values(layers).filter(l => l.type === "user");

  const toggle = (id, v) => commit(s => mutSetLayerVisible(id, v, s));
  const lock   = (id, v) => commit(s => mutSetLayerLocked(id, v, s));
  const color  = (id, c) => commit(s => mutSetLayerColor(id, c, s));
  const del    = (id)    => commit(s => mutDeleteLayer(id, s));
  const rename = (id, n) => commit(s => mutRenameLayer(id, n, s));

  const activeLayerObj = layers[activeLayer] || Object.values(layers)[0];

  return (
    <div style={{
      width: 200, background: "#04080f", borderRight: "1px solid #0d1520",
      display: "flex", flexDirection: "column", flexShrink: 0,
      fontFamily: "'JetBrains Mono',monospace",
    }}>
      {/* Tab bar */}
      <div style={{ display:"flex", borderBottom:"1px solid #0d1520", flexShrink:0, background:"#030508" }}>
        {SECTION_TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} title={t.label}
            style={{ flex:1, padding:"7px 2px", fontSize:9, border:"none",
              background: tab===t.id ? "#04080f" : "transparent",
              borderBottom: `2px solid ${tab===t.id ? "#3b82f6" : "transparent"}`,
              color: tab===t.id ? "#60a5fa" : "#1e2a36",
              cursor:"pointer", fontFamily:"inherit", fontWeight:700, letterSpacing:0.5 }}>
            {t.icon}
          </button>
        ))}
      </div>

      {/* Active layer indicator */}
      {tab === "layers" && activeLayerObj && (
        <div style={{ padding:"4px 8px", background:"#06111e", borderBottom:"1px solid #0d1520",
          display:"flex", alignItems:"center", gap:5, flexShrink:0 }}>
          <span style={{ fontSize:8, color:"#2a3a4a", letterSpacing:1 }}>CURRENT</span>
          <span style={{ width:8, height:8, borderRadius:"50%", background:activeLayerObj.color, flexShrink:0 }}/>
          <span style={{ fontSize:10, color:activeLayerObj.color, flex:1,
            overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
            {activeLayerObj.name}
          </span>
        </div>
      )}

      {/* Content */}
      <div style={{ flex:1, overflowY:"auto", display:"flex", flexDirection:"column" }}>

        {/* ── LAYERS TAB ── */}
        {tab === "layers" && (
          <>
            {/* System layers */}
            <div style={{ padding:"4px 8px 2px", fontSize:8, color:"#1e2a36", letterSpacing:1.5 }}>SYSTEM</div>
            {system.map(l => (
              <LayerRow key={l.id} layer={l}
                isActive={activeLayer === l.id}
                onSetActive={onSetActiveLayer}
                onToggleVisible={toggle} onToggleLock={lock}
                onColorChange={color} onDelete={del} onRename={rename}/>
            ))}
            {/* User layers */}
            <div style={{ padding:"8px 8px 2px", fontSize:8, color:"#1e2a36", letterSpacing:1.5 }}>USER LAYERS</div>
            {user.length === 0 && (
              <div style={{ padding:"4px 10px", fontSize:9.5, color:"#1e2a36" }}>No user layers</div>
            )}
            {user.map(l => (
              <LayerRow key={l.id} layer={l}
                isActive={activeLayer === l.id}
                onSetActive={onSetActiveLayer}
                onToggleVisible={toggle} onToggleLock={lock}
                onColorChange={color} onDelete={del} onRename={rename}/>
            ))}
            {/* Add layer input */}
            <div style={{ padding:"6px 8px", borderTop:"1px solid #0d1520", display:"flex", gap:4, flexShrink:0 }}>
              <input value={newName} onChange={e => setNewName(e.target.value)}
                onKeyDown={e => { if (e.key==="Enter" && newName.trim()) { commit(s => mutAddLayer(newName.trim(), "#94a3b8", s)); setNewName(""); }}}
                placeholder="+ new layer…"
                style={{ flex:1, background:"transparent", border:"1px solid #0d1520",
                  color:"#4a5e70", borderRadius:3, padding:"3px 6px",
                  fontSize:9.5, fontFamily:"inherit", outline:"none" }}/>
              <button onClick={() => { if(newName.trim()) { commit(s => mutAddLayer(newName.trim(), "#94a3b8", s)); setNewName(""); }}}
                style={{ background:"#0c1828", border:"1px solid #1e3d66", color:"#60a5fa",
                  borderRadius:3, padding:"3px 7px", cursor:"pointer", fontSize:11 }}>
                +
              </button>
            </div>
          </>
        )}

        {/* ── OBJECTS TAB ── */}
        {tab === "objects" && (
          <ObjectsPanel st={st} sel={sel} onSelect={onSelectObject}/>
        )}

        {/* ── SYMBOLS TAB ── */}
        {tab === "symbols" && (
          <SymbolsPanel activeSymbol={activeSymbol} onPickSymbol={onPickSymbol}/>
        )}

        {/* ── LAYER STATES TAB ── */}
        {tab === "states" && (
          <LayerStatesPanel st={st} commit={commit}/>
        )}

        {/* ── PROPERTIES TAB ── */}
        {tab === "properties" && (
          <div style={{ padding:"8px 10px", fontSize:10, color:"#4a5e70" }}>
            <div style={{ marginBottom:8, fontSize:9, color:"#2a3a4a", letterSpacing:1.5 }}>SELECTION</div>
            {!sel?.edge && !sel?.face && !sel?.node && !sel?.opening && !selAnnotation && (
              <div style={{ color:"#1e2a36", fontSize:10 }}>Nothing selected</div>
            )}
            {sel?.edge && (() => {
              const e = (st.edges||{})[sel.edge];
              if (!e) return null;
              return (
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  <PropRow label="Type" value="Wall"/>
                  <PropRow label="Material" value={e.materialId||"BRICK"} accent="#8B4513"/>
                  <PropRow label="Thickness" value={`${e.thickness}mm`}/>
                  <PropRow label="Height" value={`${(e.height||3000)/1000}m`}/>
                </div>
              );
            })()}
            {sel?.face && (() => {
              const f = (st.faces||{})[sel.face];
              const r = (st.rooms||{})[sel.face];
              if (!f) return null;
              return (
                <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                  <PropRow label="Type" value={r?.type||"ROOM"}/>
                  <PropRow label="Name" value={r?.name||"—"}/>
                  <PropRow label="Area" value={`${(f.area/1e6).toFixed(2)} m²`} accent="#4ade80"/>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}

function LayerStatesPanel({ st, commit }) {
  const [newStateName, setNewStateName] = useState("");
  const states = Object.keys(st.layerStates || {});

  const save    = name => commit(s => mutSaveLayerState(name, s));
  const restore = name => commit(s => mutRestoreLayerState(name, s));
  const del     = name => commit(s => mutDeleteLayerState(name, s));

  const iconBtn2 = { background:"transparent", border:"none", cursor:"pointer",
    fontSize:10, padding:"1px 4px", borderRadius:2, color:"#3b4a5a" };

  return (
    <div style={{ padding:"8px 0", display:"flex", flexDirection:"column", gap:0 }}>
      <div style={{ padding:"0 10px 6px", fontSize:8, color:"#1e2a36", letterSpacing:1.5 }}>
        LAYER STATE SNAPSHOTS
      </div>

      {states.length === 0 && (
        <div style={{ padding:"6px 10px", fontSize:10, color:"#1e2a36" }}>
          No saved states
        </div>
      )}

      {states.map(name => (
        <div key={name} style={{ display:"flex", alignItems:"center", gap:4,
          padding:"4px 10px", borderBottom:"1px solid #06111e" }}>
          <span style={{ flex:1, fontSize:10, color:"#6b7e94",
            fontFamily:"'JetBrains Mono',monospace",
            overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
            {name}
          </span>
          <button title="Restore state" style={{ ...iconBtn2, color:"#4ade80" }}
            onClick={() => restore(name)}>↺</button>
          <button title="Overwrite with current" style={{ ...iconBtn2, color:"#e8a838" }}
            onClick={() => save(name)}>⊙</button>
          <button title="Delete state" style={{ ...iconBtn2, color:"#f87171" }}
            onClick={() => del(name)}>✕</button>
        </div>
      ))}

      <div style={{ padding:"8px 10px", display:"flex", gap:4, borderTop:"1px solid #0d1520", marginTop:4 }}>
        <input
          value={newStateName}
          onChange={e => setNewStateName(e.target.value)}
          placeholder="State name…"
          onKeyDown={e => {
            if (e.key === "Enter" && newStateName.trim()) {
              save(newStateName.trim());
              setNewStateName("");
            }
          }}
          style={{ flex:1, fontSize:9, background:"#04080f", border:"1px solid #0d1520",
            color:"#a0b4c8", borderRadius:2, padding:"3px 6px", fontFamily:"inherit" }}
        />
        <button
          onClick={() => { if (newStateName.trim()) { save(newStateName.trim()); setNewStateName(""); } }}
          style={{ fontSize:9, padding:"2px 8px", background:"#0c1828",
            border:"1px solid #1e3050", color:"#60a5fa", borderRadius:2,
            cursor:"pointer", fontFamily:"inherit" }}>
          Save
        </button>
      </div>
      <div style={{ padding:"4px 10px", fontSize:8, color:"#1e2a36", lineHeight:1.5 }}>
        ↺ restore · ⊙ overwrite · ✕ delete
      </div>
    </div>
  );
}

function PropRow({ label, value, accent }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
      padding:"3px 0", borderBottom:"1px solid #06111e" }}>
      <span style={{ fontSize:9, color:"#2a3a4a", fontFamily:"'JetBrains Mono',monospace" }}>{label}</span>
      <span style={{ fontSize:10, color:accent||"#6b7e94", fontFamily:"'JetBrains Mono',monospace" }}>{value}</span>
    </div>
  );
}
